import { fetchSearchListItem, type ListingFetchFailure } from "../data/api/search";
import { trackError, ErrorSeverity, type ErrorContext } from "../utils/error-tracking";
import { markTabLeaving } from "../utils/tab-crash-sentinel";
import { classifyTraffic, CRAWLER_CLASSES } from "../utils/traffic-class";
import type { Product } from "../contexts/ProductProvider";
import { hardNavigate } from "../utils/hard-navigate";

/** The API answered 404: the listing was sold, archived or deleted. */
export const LISTING_GONE = "gone" as const;

/** How long to wait before asking again after the network dropped a fetch. */
export const PRODUCT_RETRY_DELAY_MS = 800;

/**
 * Resolve once it is worth fetching again: after a short pause, and not before
 * the page is visible. A backgrounded mobile tab has its network cut, so a
 * retry while hidden would fail the same way; a tab that never comes back
 * never retries, and never bounces.
 */
export function waitToRetry(delayMs: number = PRODUCT_RETRY_DELAY_MS): Promise<void> {
  return new Promise((resolve) => {
    const later = () => { setTimeout(resolve, delayMs); };
    if (typeof document === "undefined" || document.visibilityState !== "hidden") {
      later();
      return;
    }
    const onVisible = () => {
      if (document.visibilityState === "hidden") return;
      document.removeEventListener("visibilitychange", onVisible);
      later();
    };
    document.addEventListener("visibilitychange", onVisible);
  });
}

/**
 * Fetches a product by ID from the search API.
 * Returns LISTING_GONE when the API says the listing no longer exists, and null
 * when the fetch failed (logging it if it threw). `onFailure` hears why a
 * non-404 fetch came back empty, and how many attempts it took.
 *
 * A fetch the network dropped ("TypeError: Failed to fetch") is asked once
 * more before giving up. Prod 2026-10-15: after the leaving-reader fix, two of
 * three bounces were live, visible pages whose one listing request failed at
 * 1-2s; one of those readers loaded "/" 200ms later without trouble. One blip
 * on a phone was costing us a partner reader.
 */
export async function fetchProductById(
  id: string,
  onFailure?: (failure: ListingFetchFailure, attempts: number) => void,
  wait: () => Promise<void> = waitToRetry
): Promise<Product | typeof LISTING_GONE | null> {
  const seen: { failure: ListingFetchFailure | null } = { failure: null };
  const first = await fetchProductOnce(id, (f) => { seen.failure = f; });
  const failed = seen.failure;
  if (first !== null || failed === null) return first;
  if (failed.reason !== "threw") {
    onFailure?.(failed, 1);
    return null;
  }
  await wait();
  return fetchProductOnce(id, (f) => onFailure?.(f, 2));
}

async function fetchProductOnce(
  id: string,
  onFailure: (failure: ListingFetchFailure) => void
): Promise<Product | typeof LISTING_GONE | null> {
  try {
    let gone = false;
    const fetched = await fetchSearchListItem(id, { onNotFound: () => { gone = true; }, onFailure });
    if (fetched) return fetched;
    return gone ? LISTING_GONE : null;
  } catch (e) {
    trackError(
      e instanceof Error ? e : new Error(String(e)),
      ErrorSeverity.ERROR,
      { component: "ProductPage", action: "fetchProduct", productId: id } as ErrorContext
    );
    return null;
  }
}

export const PRODUCT_BOUNCE_MESSAGE = "Product page sent the reader home: the listing did not load";

/**
 * Send the reader home from a product page whose listing did not load.
 *
 * This used to be a bare `location.href = '/'`, so a reader bounced off a
 * listing left no row at all. Prod 2026-10-06: three Chrome readers left two
 * listings created that afternoon for "/" a second after opening them, and the
 * only trace was the tab-crash sentinel misreading the redirect as a crash.
 * The row says which listing, and whether the page had server data to fall back
 * on, so the next read can tell index lag from a listing that is gone.
 */
export function sendReaderHome(
  productId: string,
  detail: Record<string, string | number | boolean | null>,
  // Its own module so a test can see the navigation (utils/hard-navigate.ts).
  go: (url: string) => void = hardNavigate
): void {
  trackError(PRODUCT_BOUNCE_MESSAGE, ErrorSeverity.WARNING, {
    component: "ProductPage",
    action: "bounce-home",
    productId,
    extra: detail,
  } as ErrorContext);
  markTabLeaving();
  go("/");
}

/** Whether the reader has started leaving the page this watch was opened on. */
export interface PageLeaveWatch {
  isLeaving: () => boolean;
  stop: () => void;
}

/**
 * Watch for the reader leaving the page: pagehide or beforeunload, cleared by a
 * pageshow that brings the page back from the back/forward cache.
 */
export function watchPageLeaving(target: EventTarget = window): PageLeaveWatch {
  let leaving = false;
  const markLeaving = () => { leaving = true; };
  const markReturned = () => { leaving = false; };
  target.addEventListener("pagehide", markLeaving);
  target.addEventListener("beforeunload", markLeaving);
  target.addEventListener("pageshow", markReturned);
  return {
    isLeaving: () => leaving,
    stop: () => {
      target.removeEventListener("pagehide", markLeaving);
      target.removeEventListener("beforeunload", markLeaving);
      target.removeEventListener("pageshow", markReturned);
    },
  };
}

/**
 * The listing fetch failed: send the reader home, unless they are already on
 * their way out.
 *
 * Prod 2026-10-09..15: ~10 bounces a day, all "TypeError: Failed to fetch" on
 * the client-only /product/<id> partner link, 0.3-8s after a brand-new visitor
 * arrived. Not one of them then reached "/" — no page view followed in ten
 * minutes. The reader had pressed back or closed the tab, the browser aborted
 * the in-flight fetch, and we logged a bounce and pointed a closing tab at "/".
 * A fetch aborted by the reader leaving is not a listing that failed to load.
 *
 * A declared crawler is not sent home either. Prod 2026-10-16: the one bounce
 * after the retry fix was bingbot rendering a partner link, refused by the
 * firewall with HTTP 403. Pointing it at "/" teaches the index that the listing
 * URL is the home page, and fetchSearchListItem has already logged the 403
 * from the same browser. Returns whether it bounced.
 */
export function bounceUnlessLeaving(
  page: PageLeaveWatch,
  productId: string,
  detail: Record<string, string | number | boolean | null>,
  go?: (url: string) => void,
  // `navigator` is a global in the browser and in Node alike (21+).
  userAgent: string | null = navigator.userAgent
): boolean {
  if (page.isLeaving()) return false;
  if (CRAWLER_CLASSES.includes(classifyTraffic(userAgent).trafficClass)) return false;
  sendReaderHome(productId, {
    ...detail,
    // If a bounce still fires, these say whether the page was live when it did.
    visibility: typeof document !== "undefined" ? document.visibilityState : "unknown",
    ms_since_load: Math.round(performance.now()),
  }, go);
  return true;
}
